/**
 * Wallet Screen
 * User wallet with balance and transaction history
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  ScrollView,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  Alert,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Icon from '../components/Icon';
import { transactionAPI, userAPI, userTradesAPI } from '../services/api';
import { authAPI } from '../services/authApi';
import { useTheme } from '../context/ThemeContext';

const WalletScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('All');
  const [transactions, setTransactions] = useState([]);
  const [debitTransactions, setDebitTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [lastKnownBalance, setLastKnownBalance] = useState(0);
  const [levelData, setLevelData] = useState({
    level1Percent: 0,
    level2Percent: 0,
    level3Percent: 0,
    level1Amount: 0,
    level2Amount: 0,
    level3Amount: 0
  });

  // Helper function to replace rupee symbols with dollar symbols in text
  const replaceCurrencySymbols = (text) => {
    if (!text) return text;
    return text.replace(/₹/g, '$');
  };

  // Helper function to format debit transaction data from debits API
  const formatDebitTransaction = (txn) => {
    const now = new Date();
    const txnDate = txn.date || txn.createdAt || now.toISOString();
    
    let amount = 0;
    let description = '';
    
    // Handle different kinds of debit transactions
    switch (txn.kind) {
      case 'COMMISSION_DEDUCTED':
        // Use commission amount - match HistoryScreen field naming
        amount = parseFloat(txn.commissionAmount || txn.commission || txn.totalCommissionDeducted) || 0;
        description = 'Commission Deducted - Trade Profit';
        break;
      case 'WITHDRAWAL':
        amount = parseFloat(txn.amount) || 0;
        description = `Withdrawal - ${replaceCurrencySymbols(txn.method) || 'Method not specified'} (${txn.status || 'Status unknown'})`;
        break;
      case 'ADMIN_ADJUST':
        amount = parseFloat(txn.amount) || 0;
        description = replaceCurrencySymbols(txn.description) || 'Admin adjustment';
        break;
      default:
        amount = parseFloat(txn.amount || txn.totalCommissionDeducted) || 0;
        description = replaceCurrencySymbols(txn.description) || 'Debit transaction';
    }
    
    return {
      id: txn.id || `debit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'debit',
      amount: amount.toFixed(2),
      description: description,
      date: formatDate(txnDate),
      rawDate: txnDate,
      status: txn.status?.toLowerCase() || 'completed',
      reference: txn.id || `REF${Date.now().toString().slice(-6)}`,
      kind: txn.kind,
      rawTransaction: txn
    };
  };

  // Helper function to format transaction data from API
  const formatTransaction = (txn) => {
    const now = new Date();
    const txnDate = txn.date || txn.createdAt || txn.timestamp || now.toISOString();
    
    // Determine if this is a loss/debit transaction
    let transactionType = 'credit'; // default to credit
    
    // Calculate 100% profit - same as HistoryScreen
    let actualAmount = 0;
    
    // Check if this is a trade with price data
    if (txn.buyPrice && txn.sellPrice && txn.quantity) {
      const buyPrice = parseFloat(txn.buyPrice) || 0;
      const sellPrice = parseFloat(txn.sellPrice) || 0;
      const quantity = parseFloat(txn.quantity) || 0;
      
      // Calculate full profit (same logic as HistoryScreen)
      if (buyPrice > 0 && sellPrice > 0 && quantity > 0) {
        if (txn.tradeType === 'SELL') {
          actualAmount = (buyPrice - sellPrice) * quantity; // 100% profit for SELL
        } else if (txn.tradeType === 'BUY') {
          actualAmount = (sellPrice - buyPrice) * quantity; // 100% profit for BUY
        }
      } else {
        actualAmount = parseFloat(txn.userReceived) || 0;
      }
    } else {
      // Fallback to existing fields if no trade data
      actualAmount = parseFloat(txn.totalProfit) || parseFloat(txn.profitAndLoss) || parseFloat(txn.amount) || 0;
    }
    
    // Get description for analysis
    const description = (txn.title || txn.description || txn.note || txn.message || '').toLowerCase();
    const originalDescription = txn.title || txn.description || txn.note || txn.message || 'Transaction';
    
    console.log('Analyzing transaction description:', description);
    console.log('WalletScreen - Calculated amount (100%):', actualAmount);
    
    // Priority 1: Check for explicit loss keywords in description
    if (description.includes('loss') || 
        description.includes('stop loss') ||
        description.includes('loss from') ||
        description.includes('trading loss')) {
      console.log('Detected loss transaction from description');
      transactionType = 'debit';
      actualAmount = Math.abs(actualAmount);
    }
    // Priority 2: Check for withdrawal/debit keywords
    else if (description.includes('withdrawal') ||
        description.includes('withdraw') ||
        description.includes('debit') ||
        description.includes('fee') ||
        description.includes('charge')) {
      console.log('Detected debit transaction from description');
      transactionType = 'debit';
      actualAmount = Math.abs(actualAmount);
    }
    // Priority 3: Check transaction type fields
    else if (txn.transactionType?.toLowerCase() === 'debit') {
      console.log('Detected debit from transactionType field');
      transactionType = 'debit';
      actualAmount = Math.abs(actualAmount);
    } else if (txn.transactionType?.toLowerCase() === 'credit') {
      console.log('Detected credit from transactionType field');
      transactionType = 'credit';
      actualAmount = Math.abs(actualAmount);
    }
    // Priority 4: Check sign indicators
    else if (txn.sign === '-' || actualAmount < 0) {
      console.log('Detected debit from negative amount/sign');
      transactionType = 'debit';
      actualAmount = Math.abs(actualAmount);
    } else if (txn.sign === '+' || actualAmount > 0) {
      console.log('Detected credit from positive amount/sign');
      transactionType = 'credit';
      actualAmount = Math.abs(actualAmount);
    } 
    // Priority 5: Check other type fields
    else if (txn.type === 'loss' || txn.type === 'debit') {
      console.log('Detected debit from type field');
      transactionType = 'debit';
      actualAmount = Math.abs(actualAmount);
    }
    
    // Simplify description for trade profit transactions
    let finalDescription = replaceCurrencySymbols(originalDescription);
    if (transactionType === 'credit' && 
        (description.includes('profit') || description.includes('trade'))) {
      finalDescription = 'Trade Profit';
    }
    
    const formattedTransaction = {
      id: txn.id || txn._id || `wallet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: transactionType,
      amount: actualAmount,
      description: finalDescription,
      date: formatDate(txnDate),
      rawDate: txnDate,
      status: txn.status?.toLowerCase() === 'completed' ? 'completed' : (txn.status || 'completed').toLowerCase(),
      reference: txn.refId || txn.reference || txn.transactionId || txn.txnId || `REF${Date.now().toString().slice(-6)}`,
    };
    
    console.log('Final formatted transaction:', formattedTransaction);
    return formattedTransaction;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return new Date().toLocaleDateString('en-IN') + ' ' + new Date().toLocaleTimeString('en-IN', { hour12: false });
      }
      return date.toLocaleDateString('en-IN') + ' ' + date.toLocaleTimeString('en-IN', { hour12: false });
    } catch {
      return new Date().toLocaleDateString('en-IN') + ' ' + new Date().toLocaleTimeString('en-IN', { hour12: false });
    }
  };

  useEffect(() => {
    fetchWalletData();
  }, []);

  // Auto-refresh when screen is focused (to update balance after deposit approval)
  useFocusEffect(
    React.useCallback(() => {
      console.log('Wallet screen focused - refreshing data');
      fetchWalletData();
      
      // Set up auto-refresh every 15 seconds when screen is focused (more frequent)
      const interval = setInterval(() => {
        console.log('Auto-refreshing wallet data...');
        fetchWalletData();
      }, 15000); // 15 seconds for faster updates
      
      return () => {
        console.log('Wallet screen unfocused - clearing interval');
        clearInterval(interval);
      };
    }, [])
  );

  const fetchWalletData = async () => {
    try {
      console.log('Fetching wallet data from APIs...');
      
      const [profile, walletHistoryResponse, userTradesResponse, generalTxns, debitsResponse, referralsResponse] = await Promise.all([
        authAPI.getProfile(),
        userAPI.getWalletHistory().catch((err) => {
          console.log('Wallet history API error:', err.response?.status);
          return { transactions: [] };
        }),
        userTradesAPI.getUserTrades({ limit: 10000 }).catch((err) => {
          console.log('User Trades API error:', err.response?.status);
          return null;
        }),
        transactionAPI.getTransactions().catch((err) => {
          console.log('General transactions API error:', err.response?.status);
          return [];
        }),
        userAPI.getDebits().catch((err) => {
          console.log('Debits API error:', err.response?.status);
          return { success: false, data: [] };
        }),
        userAPI.getMyReferrals().catch((err) => {
          console.log('My referrals API error:', err.response?.status);
          return { levels: [] };
        })
      ]);
      
      console.log('Raw profile data from API:', profile);
      console.log('Raw balance value:', profile?.balance);
      console.log('Wallet history response:', walletHistoryResponse);
      console.log('User trades response:', userTradesResponse);
      console.log('General transactions:', generalTxns);
      console.log('Debits API response:', debitsResponse);
      console.log('Raw referrals API response:', referralsResponse);
      
      // Process level data for display
      let processedLevelData = {
        level1Percent: 5, // Default 5% for Level 1
        level2Percent: 3, // Default 3% for Level 2
        level3Percent: 1, // Default 1% for Level 3
        level1Amount: 0,
        level2Amount: 0,
        level3Amount: 0
      };
      
      if (referralsResponse && referralsResponse.levels && Array.isArray(referralsResponse.levels)) {
        console.log('Processing levels data:', referralsResponse.levels);
        
        referralsResponse.levels.forEach((level, index) => {
          const levelNum = level.level || (index + 1);
          const commissionPercent = parseFloat(level.commissionPercentage || level.percentage || 0);
          const usersCount = level.users ? level.users.length : 0;
          
          console.log(`Level ${levelNum}:`, { commissionPercent, usersCount });
          
          // Calculate commission amount based on current balance for now
          const currentBalance = profile?.balance || 0;
          const estimatedAmount = (currentBalance * commissionPercent) / 100;
          
          if (levelNum === 1) {
            processedLevelData.level1Percent = commissionPercent;
            processedLevelData.level1Amount = estimatedAmount;
          } else if (levelNum === 2) {
            processedLevelData.level2Percent = commissionPercent;
            processedLevelData.level2Amount = estimatedAmount;
          } else if (levelNum === 3) {
            processedLevelData.level3Percent = commissionPercent;
            processedLevelData.level3Amount = estimatedAmount;
          }
        });
      }
      
      console.log('Processed level data:', processedLevelData);
      setLevelData(processedLevelData);
      
      // Extract transactions from the response object
      const walletHistory = walletHistoryResponse?.transactions || walletHistoryResponse || [];
      const userTrades = userTradesResponse?.success && Array.isArray(userTradesResponse.data) ? userTradesResponse.data : [];
      
      // Process debits API response - show commission deductions, withdrawals, and level commissions
      let formattedDebitTransactions = [];
      if (debitsResponse && debitsResponse.success && Array.isArray(debitsResponse.data)) {
        console.log('Processing debits from debits API:', debitsResponse.data.length);
        
        // Filter to show COMMISSION_DEDUCTED, WITHDRAWAL, and LEVEL_COMMISSION transactions
        const debitTransactions = debitsResponse.data.filter(txn => 
          txn.kind === 'COMMISSION_DEDUCTED' || txn.kind === 'WITHDRAWAL' || txn.kind === 'LEVEL_COMMISSION'
        );
        console.log('Commission, withdrawal, and level commission transactions found:', debitTransactions.length);
        
        formattedDebitTransactions = debitTransactions.map((txn, index) => {
          console.log('Raw debit transaction:', txn);
          
          let amount = 0;
          let description = '';
          
          if (txn.kind === 'COMMISSION_DEDUCTED') {
            // Use commission amount - match HistoryScreen field naming
            amount = parseFloat(txn.commissionAmount || txn.commission || txn.totalCommissionDeducted) || 0;
            description = 'Commission Deducted - Trade Profit';
          } else if (txn.kind === 'WITHDRAWAL') {
            amount = parseFloat(txn.amount) || 0;
            description = `Withdrawal - ${replaceCurrencySymbols(txn.method) || 'Method not specified'} (${txn.status || 'Status unknown'})`;
          } else if (txn.kind === 'LEVEL_COMMISSION') {
            amount = parseFloat(txn.amount) || 0;
            // Extract level information from transaction
            const levelNum = txn.level || txn.levelNumber || 1;
            const commissionPercent = txn.commissionPercentage || txn.percentage || 0;
            description = `Level ${levelNum} Commission (${commissionPercent}%)`;
          }
          
          const formatted = {
            id: `debit_${txn.kind}_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`,
            type: 'debit',
            amount: amount.toFixed(2),
            description: description,
            date: formatDate(txn.date || new Date().toISOString()),
            rawDate: txn.date || new Date().toISOString(),
            status: txn.status?.toLowerCase() || 'completed',
            reference: txn.id || `REF${Date.now().toString().slice(-6)}`,
            kind: txn.kind,
            level: txn.kind === 'LEVEL_COMMISSION' ? (txn.level || txn.levelNumber || 1) : null,
            commissionPercentage: txn.kind === 'LEVEL_COMMISSION' ? (txn.commissionPercentage || txn.percentage || 0) : null,
            rawTransaction: txn
          };
          console.log('Formatted debit transaction:', formatted);
          return formatted;
        });
        console.log('Total debit transactions processed:', formattedDebitTransactions.length);
      } else {
        console.log('No debits data or invalid response format');
      }
      
      // Process and format wallet history transactions
      let formattedTransactions = [];
      
      // Priority 1: Use user trades data if available (has full trade details)
      if (Array.isArray(userTrades) && userTrades.length > 0) {
        console.log('Processing user trades (priority 1):', userTrades.length);
        formattedTransactions = userTrades.map((txn, index) => {
          console.log('Raw user trade:', txn);
          const formatted = formatTransaction(txn);
          formatted.id = `trade_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;
          console.log('Formatted user trade with 100% profit:', formatted);
          return formatted;
        });
        console.log('Total transactions from user trades:', formattedTransactions.length);
      }
      // Priority 2: Use wallet history if trades not available
      else if (Array.isArray(walletHistory) && walletHistory.length > 0) {
        console.log('Processing wallet history transactions:', walletHistory.length);
        formattedTransactions = walletHistory.map((txn, index) => {
          console.log('Raw transaction:', txn);
          const formatted = formatTransaction(txn);
          // Add index to ensure unique ID even if processed at same timestamp
          formatted.id = `wallet_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;
          console.log('Formatted transaction:', formatted);
          return formatted;
        });
        console.log('Formatted wallet history:', formattedTransactions);
        console.log('Total transactions from wallet history:', formattedTransactions.length);
      } else if (Array.isArray(generalTxns) && generalTxns.length > 0) {
        // Fallback to general transactions if wallet history is empty
        console.log('Processing general transactions as fallback:', generalTxns.length);
        formattedTransactions = generalTxns.map((txn, index) => {
          console.log('Raw general transaction:', txn);
          const formatted = formatTransaction(txn);
          // Add index to ensure unique ID even if processed at same timestamp
          formatted.id = `general_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;
          console.log('Formatted general transaction:', formatted);
          return formatted;
        });
        console.log('Using general transactions as fallback');
      }
      
      // Create separate level commission entries for Trade Profit transactions
      const expandedTransactions = [];
      formattedTransactions.forEach(txn => {
        expandedTransactions.push(txn);
        
        // If this is a Trade Profit transaction, create separate level commission entries
        if (txn.type === 'credit' && txn.description === 'Trade Profit' && txn.amount > 0) {
          const tradeAmount = parseFloat(txn.amount) || 0;
          const baseDate = new Date(txn.rawDate || txn.date);
          
          // Create Level 1 commission entry if percentage > 0
          if (levelData.level1Percent > 0) {
            const level1Amount = (tradeAmount * levelData.level1Percent) / 100;
            expandedTransactions.push({
              id: `level1_${txn.id}_${Date.now()}`,
              type: 'credit',
              amount: level1Amount,
              description: `Level 1 Commission (${levelData.level1Percent}%)`,
              date: txn.date,
              rawDate: txn.rawDate,
              status: txn.status,
              reference: `L1-${txn.reference}`,
              kind: 'LEVEL_COMMISSION',
              level: 1,
              commissionPercentage: levelData.level1Percent,
              parentTradeId: txn.id
            });
          }
          
          // Create Level 2 commission entry if percentage > 0
          if (levelData.level2Percent > 0) {
            const level2Amount = (tradeAmount * levelData.level2Percent) / 100;
            expandedTransactions.push({
              id: `level2_${txn.id}_${Date.now()}`,
              type: 'credit',
              amount: level2Amount,
              description: `Level 2 Commission (${levelData.level2Percent}%)`,
              date: txn.date,
              rawDate: txn.rawDate,
              status: txn.status,
              reference: `L2-${txn.reference}`,
              kind: 'LEVEL_COMMISSION',
              level: 2,
              commissionPercentage: levelData.level2Percent,
              parentTradeId: txn.id
            });
          }
          
          // Create Level 3 commission entry if percentage > 0
          if (levelData.level3Percent > 0) {
            const level3Amount = (tradeAmount * levelData.level3Percent) / 100;
            expandedTransactions.push({
              id: `level3_${txn.id}_${Date.now()}`,
              type: 'credit',
              amount: level3Amount,
              description: `Level 3 Commission (${levelData.level3Percent}%)`,
              date: txn.date,
              rawDate: txn.rawDate,
              status: txn.status,
              reference: `L3-${txn.reference}`,
              kind: 'LEVEL_COMMISSION',
              level: 3,
              commissionPercentage: levelData.level3Percent,
              parentTradeId: txn.id
            });
          }
          
          // Update the original Trade Profit transaction to show remaining amount
          const totalCommissionCut = (levelData.level1Percent + levelData.level2Percent + levelData.level3Percent);
          if (totalCommissionCut > 0) {
            const remainingAmount = tradeAmount - ((tradeAmount * totalCommissionCut) / 100);
            txn.amount = remainingAmount;
            txn.description = `Trade Profit (Your Share)`;
            txn.commissionNote = `${totalCommissionCut}% distributed to levels`;
          }
        }
      });
      
      console.log('Expanded transactions with levels:', expandedTransactions.length);
      console.log('Level commission entries:', expandedTransactions.filter(t => t.kind === 'LEVEL_COMMISSION').length);
      
      // Combine expanded transactions and debit transactions
      const allTransactions = [...expandedTransactions, ...formattedDebitTransactions];
      
      // Sort all transactions by date (newest first)
      allTransactions.sort((a, b) => new Date(b.rawDate || b.date) - new Date(a.rawDate || a.date));
      
      console.log('Combined transaction count:', allTransactions.length);
      console.log('Credit transactions:', allTransactions.filter(t => t.type === 'credit').length);
      console.log('Debit transactions:', allTransactions.filter(t => t.type === 'debit').length);
      
      // Check if balance increased and show notification
      if (userProfile && profile && profile.balance > userProfile.balance) {
        const increase = profile.balance - userProfile.balance;
        console.log('Balance increased by:', increase);
        setTimeout(() => {
          Alert.alert(
            '💰 Balance Updated!',
            `Your wallet has been credited with $${increase.toFixed(2)}\n\nNew Balance: $${profile.balance.toFixed(2)}`,
            [{ text: 'Great!', style: 'default' }]
          );
        }, 1000);
      }
      
      setUserProfile(profile);
      setTransactions(allTransactions);
      setDebitTransactions(formattedDebitTransactions);
      
      console.log('Final combined transaction count:', allTransactions.length);
      
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      if (error.response?.status === 401) {
        await authAPI.logout();
        navigation.replace('Login');
      }
      // Set empty array on error
      setTransactions([]);
      setDebitTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    console.log('Pull to refresh triggered');
    setIsRefreshing(true);
    try {
      await fetchWalletData();
      // Also try to force refresh from server
      await new Promise(resolve => setTimeout(resolve, 1000)); // Small delay to show refresh
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const walletBalance = {
    total: userProfile?.balance || 0,
    available: ((userProfile?.balance || 0) * 0.89),
    locked: ((userProfile?.balance || 0) * 0.11),
  };

  console.log('Wallet balance calculation:', {
    rawBalance: userProfile?.balance,
    total: walletBalance.total,
    available: walletBalance.available,
    locked: walletBalance.locked
  });

  // Use real API transactions when available, NO mock data for new users
  const baseTransactions = transactions.length > 0 ? 
    transactions.map(txn => {
      // Force override for loss transactions that might not be detected properly
      if (txn.description && txn.description.toLowerCase().includes('loss')) {
        console.log('Force overriding loss transaction:', txn.description);
        return { ...txn, type: 'debit' };
      }
      return txn;
    }) : 
    // Empty array for new users - no mock transactions
    [];

  // Check if we already have level commission transactions from API
  const hasRealLevelCommissions = baseTransactions.some(t => t.kind === 'LEVEL_COMMISSION');
  
  // Use only real transactions from API
  const displayTransactions = baseTransactions;
  
  console.log('Final displayTransactions:', displayTransactions.length);
  console.log('Base transactions:', baseTransactions.length);
  console.log('Has real level commissions:', hasRealLevelCommissions);
  console.log('Level commission entries:', displayTransactions.filter(t => t.kind === 'LEVEL_COMMISSION').length);

  const filterTransactions = () => {
    const txnList = displayTransactions || [];
    if (activeTab === 'All') return txnList;
    if (activeTab === 'Credit') return txnList.filter(t => t.type === 'credit');
    if (activeTab === 'Debit') return txnList.filter(t => t.type === 'debit');
    return txnList;
  };

  const renderTransaction = ({ item }) => {
    // Check if this is a level commission transaction first
    const isLevelCommission = item.kind === 'LEVEL_COMMISSION' || 
                              (item.description && (item.description.includes('Level') && item.description.includes('Commission'))) ||
                              item.level > 0;
                              
    const isLoss = item.type === 'debit' || isLevelCommission; // Level commissions are always deductions
    const amountColor = isLoss ? colors.error : colors.success;
    const displayAmount = Math.abs(item.amount).toFixed(2);
    const amountPrefix = isLoss ? '-' : '+';
    
    const levelNumber = item.level || (item.description ? parseInt(item.description.match(/Level (\d+)/)?.[1]) : null);
    const commissionPercent = item.commissionPercentage || 0;
    
    console.log('🔍 Level Commission Detection:', {
      id: item.id,
      description: item.description,
      kind: item.kind,
      level: item.level,
      commissionPercentage: item.commissionPercentage,
      isLevelCommission: isLevelCommission,
      levelNumber: levelNumber
    });
    
    // Check if this is a trade profit transaction (simplified - no breakdown needed)
    const isTradeProfit = item.type === 'credit' && (item.description && (item.description.includes('Profit') || item.description.includes('Your Share')));
    
    return (
      <View style={styles.transactionItem}>
        <View style={[
          styles.transactionIcon,
          { backgroundColor: isLevelCommission ? `${colors.error}33` : (isLoss ? `${colors.error}33` : `${colors.success}33`) }
        ]}>
          <Icon
            name={isLevelCommission ? 'people-outline' : (isTradeProfit ? 'trending-up' : (isLoss ? 'arrow-down' : 'arrow-up'))}
            size={20}
            color={isLevelCommission ? colors.error : (isLoss ? colors.error : colors.success)}
          />
        </View>

        <View style={styles.transactionDetails}>
          <Text style={[styles.transactionDescription, { color: colors.text }]}>{item.description}</Text>
          
          {/* Simple Level Commission Display */}
          {isLevelCommission && (
            <Text style={[styles.levelCommissionNote, { color: colors.textSecondary, fontSize: 12, marginTop: 2 }]}>
              Level {levelNumber} • {commissionPercent}% Commission Cut
            </Text>
          )}
          
          {/* Show commission note for Trade Profit transactions with commission cuts */}
          {isTradeProfit && item.commissionNote && (
            <Text style={[styles.commissionNoteText, { color: colors.textSecondary, fontSize: 12, fontStyle: 'italic' }]}>
              {item.commissionNote}
            </Text>
          )}
          
          {/* Date/time hidden as requested */}
        </View>

        <View style={styles.transactionRight}>
          <Text style={[
            styles.transactionAmount,
            { 
              color: isLevelCommission ? colors.error : amountColor, 
              fontWeight: isLevelCommission ? '800' : '700',
              fontSize: isLevelCommission ? 18 : 16
            }
          ]}>
            {amountPrefix}${displayAmount}
          </Text>
          
          <View style={[
            styles.statusBadge,
            { backgroundColor: item.status === 'completed' ? 
                (isLoss ? `${colors.error}33` : `${colors.success}33`) : 
                `${colors.warning}33` 
            }
          ]}>
            <Text style={[
              styles.statusText,
              { color: item.status === 'completed' ? 
                  (isLoss ? colors.error : colors.success) : 
                  colors.warning 
              }
            ]}>
              {item.status.toUpperCase()}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.background, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Wallet</Text>
        <TouchableOpacity 
          style={styles.headerIcon}
          onPress={() => {
            console.log('Manual refresh triggered');
            fetchWalletData();
          }}
        >
          <Icon 
            name={isRefreshing ? "sync" : "refresh"} 
            size={24} 
            color={colors.primary} 
            style={isRefreshing ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </TouchableOpacity>
      </View>

      <ScrollView 
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} tintColor={colors.primary} />
        }
      >
        {isLoading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={{ color: colors.textSecondary, marginTop: 10 }}>Loading wallet...</Text>
          </View>
        ) : null}
        {/* Balance Card */}
        <View style={[styles.balanceCard, { backgroundColor: colors.cardBackground }]}>
          <Icon name="wallet" size={48} color={colors.primary} />
          <Text style={[styles.balanceLabel, { color: colors.textSecondary }]}>Total Balance</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Text style={[
              styles.balanceAmount, 
              { 
                color: walletBalance.total >= 0 ? colors.text : colors.error 
              }
            ]}>
              {walletBalance.total < 0 ? '-' : ''}${Math.abs(walletBalance.total).toFixed(2)}
            </Text>
            {isRefreshing && (
              <ActivityIndicator size="small" color={colors.primary} />
            )}
          </View>
          
          {/* Last updated time */}
          <Text style={[styles.lastUpdated, { color: colors.textTertiary, fontSize: 11, marginTop: 4 }]}>
            Last updated: {new Date().toLocaleTimeString()}
          </Text>
        </View>

        {/* Quick Actions */}
        <View style={styles.quickActions}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => navigation.navigate('Deposit')}>
            <View style={[styles.actionIcon, { backgroundColor: `${colors.success}33` }]}>
              <Icon name="add-circle" size={24} color={colors.success} />
            </View>
            <Text style={[styles.actionText, { color: colors.text }]}>Deposit</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => navigation.navigate('Withdrawal')}>
            <View style={[styles.actionIcon, { backgroundColor: `${colors.primary}33` }]}>
              <Icon name="arrow-up-circle" size={24} color={colors.primary} />
            </View>
            <Text style={[styles.actionText, { color: colors.text }]}>Withdraw</Text>
          </TouchableOpacity>
        </View>

        {/* Tabs */}
        <View style={[styles.tabsContainer, { backgroundColor: colors.cardBackground }]}>
          {['All', 'Credit', 'Debit'].map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[styles.tab, activeTab === tab && { backgroundColor: colors.primary }]}
              onPress={() => setActiveTab(tab)}>
              <Text style={[styles.tabText, { color: colors.textTertiary }, activeTab === tab && { color: colors.text }]}>
                {tab}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Transaction History */}
        <View style={styles.transactionsSection}>
          <View style={styles.sectionHeader}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>Transaction History</Text>
            <Text style={[styles.transactionCount, { color: colors.textSecondary }]}>({filterTransactions().length})</Text>
          </View>
          
          {isLoading ? (
            <View style={styles.emptyState}>
              <ActivityIndicator size="small" color={colors.primary} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>Loading transactions...</Text>
            </View>
          ) : filterTransactions().length === 0 ? (
            <View style={styles.emptyState}>
              <Icon name="receipt-outline" size={48} color={colors.textTertiary} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                {walletBalance.total <= 0 ? 'No transaction history' : 'No transactions found'}
              </Text>
              <Text style={[styles.emptySubtext, { color: colors.textTertiary }]}>Your transaction history will appear here</Text>
            </View>
          ) : (
            <FlatList
              data={filterTransactions()}
              renderItem={renderTransaction}
              keyExtractor={(item) => item.id || item._id}
              scrollEnabled={false}
              showsVerticalScrollIndicator={false}
            />
          )}
        </View>

        <View style={{ height: 100 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0A0E27',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 50,
    paddingBottom: 16,
    backgroundColor: '#0A0E27',
    borderBottomWidth: 1,
    borderBottomColor: '#1A1F3A',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  headerIcon: {
    padding: 4,
  },
  balanceCard: {
    backgroundColor: '#1A1F3A',
    marginHorizontal: 16,
    marginTop: 20,
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
  },
  balanceLabel: {
    color: '#9CA3AF',
    fontSize: 14,
    marginTop: 12,
  },
  balanceAmount: {
    color: '#fff',
    fontSize: 36,
    fontWeight: '700',
    marginTop: 8,
  },
  balanceBreakdown: {
    flexDirection: 'row',
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#2A2F4A',
    width: '100%',
  },
  balanceItem: {
    flex: 1,
    alignItems: 'center',
    gap: 4,
  },
  balanceDivider: {
    width: 1,
    backgroundColor: '#2A2F4A',
  },
  balanceItemLabel: {
    color: '#9CA3AF',
    fontSize: 11,
  },
  balanceItemValue: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  levelCard: {
    backgroundColor: '#1A1F3A',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 16,
    padding: 20,
  },
  levelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  levelTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  levelInfoContainer: {
    gap: 12,
  },
  levelDetailCard: {
    backgroundColor: '#0A0E27',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#2A2F4A',
  },
  levelHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  levelLabel: {
    color: '#9CA3AF',
    fontSize: 14,
    fontWeight: '500',
  },
  levelPercent: {
    fontSize: 18,
    fontWeight: '700',
  },
  levelAmount: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  levelDescription: {
    color: '#6B7280',
    fontSize: 12,
    fontStyle: 'italic',
  },
  totalCommissionCard: {
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
  },
  totalCommissionLabel: {
    color: '#9CA3AF',
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
  },
  totalCommissionPercent: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  totalCommissionAmount: {
    fontSize: 16,
    fontWeight: '600',
  },
  levelGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  levelItem: {
    flex: 1,
    alignItems: 'center',
    gap: 6,
  },
  levelDivider: {
    width: 1,
    height: 40,
    backgroundColor: '#2A2F4A',
    marginHorizontal: 12,
  },
  quickActions: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 20,
    gap: 12,
  },
  actionButton: {
    flex: 1,
    alignItems: 'center',
    gap: 8,
  },
  actionIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  tabsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#1A1F3A',
    gap: 8,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#4A90E2',
  },
  tabText: {
    color: '#6B7280',
    fontSize: 14,
    fontWeight: '600',
  },
  activeTabText: {
    color: '#fff',
  },
  transactionsSection: {
    paddingHorizontal: 16,
    paddingTop: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  transactionCount: {
    fontSize: 12,
    fontWeight: '500',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
    fontWeight: '500',
  },
  emptySubtext: {
    fontSize: 12,
    textAlign: 'center',
  },
  transactionItem: {
    flexDirection: 'row',
    backgroundColor: 'rgba(26, 31, 58, 0.6)',
    padding: 16,
    marginBottom: 12,
    borderRadius: 12,
    alignItems: 'center',
  },
  transactionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  creditIconBg: {
    backgroundColor: '#00E67620',
  },
  debitIconBg: {
    backgroundColor: '#FF525220',
  },
  transactionDetails: {
    flex: 1,
  },
  transactionDescription: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  transactionDate: {
    color: '#6B7280',
    fontSize: 11,
    marginBottom: 2,
  },
  transactionReference: {
    color: '#6B7280',
    fontSize: 10,
  },
  transactionRight: {
    alignItems: 'flex-end',
  },
  transactionAmount: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
    textAlign: 'right',
  },
  creditAmount: {
    color: '#00E676',
  },
  debitAmount: {
    color: '#FF5252',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  statusCompleted: {
    backgroundColor: '#00E67620',
  },
  statusPending: {
    backgroundColor: '#FF980020',
  },
  statusText: {
    fontSize: 9,
    fontWeight: '700',
  },
  statusCompletedText: {
    color: '#00E676',
  },
  statusPendingText: {
    color: '#FF9800',
  },
  levelBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 4,
    gap: 8,
  },
  levelBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    backgroundColor: '#4A90E220',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  levelCommissionPercent: {
    fontSize: 10,
    fontWeight: '500',
    fontStyle: 'italic',
  },
  levelCommissionContainer: {
    marginVertical: 6,
    gap: 4,
  },
  levelBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  commissionBadge: {
    alignSelf: 'flex-start',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  commissionBadgeText: {
    fontSize: 10,
    fontWeight: '600',
  },
  levelCommissionAmount: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: 2,
  },
  levelCommissionNote: {
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 2,
  },
  commissionNoteText: {
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#2A2F4A',
  },
  levelPercentBadge: {
    fontSize: 12,
    fontWeight: '700',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
    textAlign: 'center',
    marginVertical: 2,
  },
  commissionBreakdownContainer: {
    marginVertical: 8,
    padding: 10,
    backgroundColor: '#1A1F3A50',
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#FF5252',
  },
  commissionBreakdownTitle: {
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 6,
  },
  commissionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 2,
  },
  commissionLevelText: {
    fontSize: 10,
    fontWeight: '500',
  },
  commissionAmountText: {
    fontSize: 10,
    fontWeight: '600',
  },
  commissionTotalText: {
    fontSize: 11,
    fontWeight: '600',
  },
  commissionTotalAmount: {
    fontSize: 11,
    fontWeight: '700',
  },
  commissionRemainingText: {
    fontSize: 11,
    fontWeight: '600',
  },
  commissionRemainingAmount: {
    fontSize: 11,
    fontWeight: '700',
  },
  totalCutBadge: {
    fontSize: 10,
    fontWeight: '600',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    textAlign: 'center',
    marginVertical: 1,
  },
});

export default WalletScreen;
